import React, { useEffect, useState } from 'react';
import { Slider } from '../ui/Slider';
import { Play, Pause, ChevronLeft, ChevronRight, RotateCcw, Calendar, ChevronDown } from 'lucide-react';
import { formatSimulatedMonthDate, parseYearMonth, UKRAINIAN_MONTH_NAMES } from '../../utils/formatters';
import { useSimulationStore } from '../../store/useSimulationStore';

export interface MonthSliderProps {
  currentMonth: number;
  maxMonths: number;
  onChange: (month: number) => void;
  simulationStartDate?: string;
  onSimulationStartDateChange?: (dateStr: string) => void;
  className?: string;
}

const YEARS_LIST = [2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035];

export const MonthSlider: React.FC<MonthSliderProps> = ({
  currentMonth,
  maxMonths,
  onChange,
  simulationStartDate,
  onSimulationStartDateChange,
  className = '',
}) => {
  const storeStartDate = useSimulationStore((s) => s.simulationStartDate);
  const storeSetStartDate = useSimulationStore((s) => s.setSimulationStartDate);

  const effectiveStartDate = simulationStartDate ?? storeStartDate;
  const handleStartDateChange = onSimulationStartDateChange ?? storeSetStartDate;

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(800); // ms per month

  const startDateObj = parseYearMonth(effectiveStartDate);
  const startYear = startDateObj.getFullYear();
  const startMonth = startDateObj.getMonth() + 1; // 1-12

  const simulatedDate = formatSimulatedMonthDate(currentMonth, effectiveStartDate);

  const handleMonthChange = (newMonth: number) => {
    const monthStr = String(newMonth).padStart(2, '0');
    handleStartDateChange(`${startYear}-${monthStr}`);
  };

  const handleYearChange = (newYear: number) => {
    const monthStr = String(startMonth).padStart(2, '0');
    handleStartDateChange(`${newYear}-${monthStr}`);
  };

  const handleResetToToday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    handleStartDateChange(`${year}-${month}`);
  };

  // Auto-playback loop
  useEffect(() => {
    if (!isPlaying) return;

    if (currentMonth >= maxMonths) {
      setIsPlaying(false);
      return;
    }

    const interval = setInterval(() => {
      if (currentMonth + 1 >= maxMonths) {
        onChange(maxMonths);
        setIsPlaying(false);
      } else {
        onChange(currentMonth + 1);
      }
    }, playbackSpeed);

    return () => clearInterval(interval);
  }, [isPlaying, currentMonth, maxMonths, onChange, playbackSpeed]);

  const handleTogglePlay = () => {
    if (!isPlaying) {
      if (currentMonth >= maxMonths) {
        onChange(1);
      }
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };

  const handlePrev = () => {
    onChange(Math.max(1, currentMonth - 1));
  };

  const handleNext = () => {
    onChange(Math.min(maxMonths, currentMonth + 1));
  };

  const handleReset = () => {
    setIsPlaying(false);
    onChange(1);
  };

  // Quick jump milestones
  const milestones = [
    { label: '6 міс', value: 6 },
    { label: '1 рік (12 міс)', value: 12 },
    { label: '2 роки (24 міс)', value: 24 },
    { label: '3 роки (36 міс)', value: 36 },
    { label: '5 років (60 міс)', value: 60 },
  ].filter((m) => m.value <= maxMonths);

  return (
    <div className={`glass-panel rounded-2xl p-3.5 sm:p-4 border border-white/10 space-y-3.5 ${className}`}>
      {/* Header: Month badge, Calendar Date & Playback controls */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <div className="flex items-baseline gap-1.5 shrink-0">
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Місяць:</span>
            <span className="text-base font-black font-mono text-accent-neon">
              {currentMonth}
            </span>
            <span className="text-xs text-text-muted font-mono">/ {maxMonths}</span>
          </div>

          {/* Interactive Date & Start Date Selector Trigger */}
          <button
            type="button"
            data-testid="sim-start-date-btn"
            onClick={() => setIsDatePickerOpen((prev) => !prev)}
            className={`
              inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-lg border shadow-sm whitespace-nowrap cursor-pointer transition-all
              ${
                isDatePickerOpen
                  ? 'bg-accent-emerald text-slate-950 border-accent-emerald font-bold'
                  : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-accent-neon border-emerald-500/25 hover:border-emerald-500/50'
              }
            `}
            title="Натисніть, щоб обрати дату початку симуляції"
          >
            <Calendar className={`w-3 h-3 shrink-0 ${isDatePickerOpen ? 'text-slate-950' : 'text-accent-emerald'}`} />
            <span>{simulatedDate}</span>
            <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isDatePickerOpen ? 'rotate-180 text-slate-950' : 'text-emerald-400/70'}`} />
          </button>
        </div>

        {/* Speed Toggle */}
        <div className="flex items-center gap-0.5 bg-slate-900/80 p-0.5 rounded-lg border border-white/10 text-[10px] font-mono">
          <button
            type="button"
            onClick={() => setPlaybackSpeed(1200)}
            className={`px-1.5 py-0.5 rounded transition-all ${playbackSpeed === 1200 ? 'bg-accent-emerald text-slate-950 font-bold' : 'text-text-muted hover:text-white'}`}
          >
            1x
          </button>
          <button
            type="button"
            onClick={() => setPlaybackSpeed(600)}
            className={`px-1.5 py-0.5 rounded transition-all ${playbackSpeed === 600 ? 'bg-accent-emerald text-slate-950 font-bold' : 'text-text-muted hover:text-white'}`}
          >
            2x
          </button>
          <button
            type="button"
            onClick={() => setPlaybackSpeed(300)}
            className={`px-1.5 py-0.5 rounded transition-all ${playbackSpeed === 300 ? 'bg-accent-emerald text-slate-950 font-bold' : 'text-text-muted hover:text-white'}`}
          >
            3x
          </button>
        </div>
      </div>

      {/* Expandable Start Date Selector */}
      {isDatePickerOpen && (
        <div
          data-testid="start-date-picker-panel"
          className="p-3 rounded-xl bg-slate-900/95 border border-accent-emerald/40 space-y-2.5 text-xs shadow-lg animate-in fade-in slide-in-from-top-1 duration-150"
        >
          <div className="flex items-center justify-between">
            <span className="text-text-primary font-bold text-xs flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-accent-neon" />
              <span>Початок симуляції (Старт):</span>
            </span>
            <button
              type="button"
              onClick={handleResetToToday}
              className="text-[11px] text-accent-neon hover:text-white flex items-center gap-1 font-mono transition-colors cursor-pointer"
              title="Скинути дату на поточний місяць"
            >
              <RotateCcw className="w-3 h-3" />
              <span>Сьогодні</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10.5px] text-text-muted mb-1 font-mono">Місяць старту:</label>
              <select
                value={startMonth}
                onChange={(e) => handleMonthChange(Number(e.target.value))}
                aria-label="Місяць початку симуляції"
                className="w-full bg-slate-950 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-text-primary focus:border-accent-neon focus:outline-none font-mono cursor-pointer"
              >
                {UKRAINIAN_MONTH_NAMES.map((name, idx) => (
                  <option key={idx} value={idx + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10.5px] text-text-muted mb-1 font-mono">Рік старту:</label>
              <select
                value={startYear}
                onChange={(e) => handleYearChange(Number(e.target.value))}
                aria-label="Рік початку симуляції"
                className="w-full bg-slate-950 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-text-primary focus:border-accent-neon focus:outline-none font-mono cursor-pointer"
              >
                {YEARS_LIST.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pt-1.5 text-[11px] text-text-muted flex items-center justify-between border-t border-white/10 font-mono">
            <span>Розрахована дата ({currentMonth}-й міс):</span>
            <span className="font-bold text-accent-neon">{simulatedDate}</span>
          </div>
        </div>
      )}

      {/* Range Slider */}
      <Slider
        value={currentMonth}
        min={1}
        max={maxMonths}
        step={1}
        valueFormatter={(val) => `${val}-й місяць`}
        onChange={onChange}
      />

      {/* Bottom controls: Playback & Milestones */}
      <div className="space-y-2 pt-1 border-t border-white/5">
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1.5">
            {/* Play/Pause Button */}
            <button
              type="button"
              data-tour="timeline-play-btn"
              onClick={handleTogglePlay}
              className={`
                w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 cursor-pointer
                ${
                  isPlaying
                    ? 'bg-amber-500 text-slate-950 shadow-[0_0_12px_rgba(245,158,11,0.4)]'
                    : 'bg-accent-emerald text-slate-950 shadow-[0_0_12px_rgba(16,185,129,0.3)] hover:scale-105'
                }
              `}
              aria-label={isPlaying ? 'Пауза' : 'Відтворити'}
            >
              {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
            </button>

            {/* Step buttons */}
            <button
              type="button"
              onClick={handlePrev}
              disabled={currentMonth <= 1}
              className="w-8 h-8 rounded-lg bg-surface-hover text-text-secondary hover:text-text-primary disabled:opacity-30 flex items-center justify-center border border-white/5 cursor-pointer"
              aria-label="Попередній місяць"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={currentMonth >= maxMonths}
              className="w-8 h-8 rounded-lg bg-surface-hover text-text-secondary hover:text-text-primary disabled:opacity-30 flex items-center justify-center border border-white/5 cursor-pointer"
              aria-label="Наступний місяць"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="w-8 h-8 rounded-lg bg-surface-hover text-text-secondary hover:text-text-primary flex items-center justify-center border border-white/5 cursor-pointer"
              title="Скинути"
              aria-label="Скинути"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>

          <span className="text-[11px] text-text-muted font-mono">
            {currentMonth} / {maxMonths} міс
          </span>
        </div>

        {/* Dedicated Full-Width Responsive Milestone Jump Row */}
        <div className="flex items-center gap-1 w-full">
          {milestones.map((m) => (
            <button
              key={m.value}
              type="button"
              data-tour={m.value === 12 ? 'milestone-12m' : undefined}
              onClick={() => onChange(m.value)}
              className={`
                flex-1 min-w-0 h-[26px] min-h-[26px] flex items-center justify-center px-0.5 py-0.5 rounded-md text-[10.5px] sm:text-[11px] font-mono transition-all cursor-pointer border whitespace-nowrap leading-none box-border text-center
                ${
                  currentMonth === m.value
                    ? 'bg-accent-emerald text-slate-950 font-bold border-accent-emerald shadow-[0_0_8px_rgba(16,185,129,0.3)]'
                    : 'bg-surface-hover text-text-secondary hover:text-text-primary border-white/5 hover:border-white/20'
                }
              `}
            >
              {m.value}м
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
